import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import e2iApi from '../../services/e2iApi';
import type { PartyRef, ProjectOption } from '../../types/e2i';
import { errorText } from '../../utils/e2i';
import ProjectSearchPicker from './ProjectSearchPicker';

interface Props {
  id: string;
  companyId: number;
  /** The enquiry's project (main job): the job takes it or one of its children. */
  mainProject: PartyRef | null;
  value: PartyRef | null;
  onChange: (value: PartyRef | null) => void;
  disabled?: boolean;
}

/** The job's SAP project: the enquiry's main project or one of its sub-projects (read live from SAP). */
const JobProjectPicker: React.FC<Props> = ({ id, companyId, mainProject, value, onChange, disabled }) => {
  const [children, setChildren] = useState<ProjectOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mainCode = mainProject?.code || '';

  useEffect(() => {
    if (!mainCode || !companyId) { setChildren([]); return undefined; }
    let cancelled = false;
    setChildren(null);
    setError(null);
    e2iApi.projects(companyId, '', mainCode)
      .then((rows) => { if (!cancelled) setChildren(rows); })
      .catch((e: any) => { if (!cancelled) { setChildren([]); setError(errorText(e, 'The sub-projects could not be read from SAP')); } });
    return () => { cancelled = true; };
  }, [companyId, mainCode]);

  const options: PartyRef[] = useMemo(() => {
    const list: PartyRef[] = mainProject ? [{ code: mainProject.code, name: mainProject.name }] : [];
    for (const c of children || []) if (!list.some((p) => p.code === c.code)) list.push({ code: c.code, name: c.name });
    if (value && !list.some((p) => p.code === value.code)) list.push(value);
    return list;
  }, [mainProject, children, value]);

  if (!mainCode) {
    return <ProjectSearchPicker id={id} companyId={companyId} label="Project" value={value} onChange={onChange} disabled={disabled} />;
  }

  return (
    <Autocomplete
      id={id}
      size="small"
      disabled={disabled}
      options={options}
      loading={children === null}
      value={value}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
      onChange={(_e, v) => onChange(v ? { code: v.code, name: v.name } : null)}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Project"
          required
          error={!!error}
          helperText={error || `${mainProject?.code} or one of its sub-projects`}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        />
      )}
    />
  );
};

export default JobProjectPicker;
